import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1, width: "100%", height: "100%", overflow: "auto",
    background: theme.palette.type === "dark" ? "#080E1C" : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles,
  },
  inner: {
    width: "100%",
    maxWidth: 900,
    margin: "0 auto",
    padding: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  pageTitle: { fontWeight: 700, fontSize: "1.4rem", letterSpacing: "-0.3px", color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A", marginBottom: 4 },
  pageSub: { fontSize: "0.88rem", color: "#64748B" },
  card: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    borderRadius: 16, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  cardTitle: { fontWeight: 700, fontSize: "0.92rem", color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A", marginBottom: 4 },
  balanceNum: { fontWeight: 800, fontSize: "2.2rem", letterSpacing: "-1px", color: "#1D4ED8", lineHeight: 1 },
  track: { height: 8, borderRadius: 4, background: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0", overflow: "hidden", margin: "12px 0 6px" },
  fill: { height: "100%", borderRadius: 4, transition: "width 0.6s ease" },
  packagesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 },
  packageCard: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    borderRadius: 16, padding: "20px", textAlign: "center",
    position: "relative", transition: "all 0.15s",
    "&:hover": { borderColor: "rgba(29,78,216,0.3)", transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
  },
  packageHighlight: { borderColor: "#1D4ED8 !important", boxShadow: "0 0 0 2px rgba(29,78,216,0.15)" },
  badge: {
    position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
    background: "#1D4ED8", color: "#fff", borderRadius: 20,
    padding: "3px 12px", fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap",
  },
  packageCredits: { fontWeight: 800, fontSize: "1.6rem", color: "#1D4ED8", letterSpacing: "-0.5px" },
  packagePrice: { fontWeight: 700, fontSize: "1.1rem", color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A", margin: "6px 0 14px" },
  buyBtn: {
    width: "100%", borderRadius: 10, fontWeight: 700, fontSize: "0.84rem",
    background: "#1D4ED8", color: "#fff", border: "none", padding: "10px", cursor: "pointer",
    transition: "all 0.15s", "&:hover": { background: "#1E40AF" },
  },
  buyBtnOutline: {
    width: "100%", borderRadius: 10, fontWeight: 600, fontSize: "0.84rem",
    background: "transparent", color: "#1D4ED8",
    border: "1px solid rgba(29,78,216,0.3)", padding: "10px", cursor: "pointer",
  },
  historyTable: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "8px 12px", textAlign: "left", fontSize: "0.72rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px", color: "#94A3B8",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F1F5F9"}`,
  },
  td: {
    padding: "10px 12px", fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#374151",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC"}`,
  },
  statRow: { display: "flex", gap: 16, flexWrap: "wrap" },
  statCard: {
    flex: 1, minWidth: 140,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#E8EDF5"}`,
    borderRadius: 12, padding: "14px 16px",
  },
  statNum: { fontWeight: 800, fontSize: "1.4rem", color: "#1D4ED8" },
  statLabel: { fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 },

  ledgerSummary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },

  ledgerSummaryCard: {
    padding: "14px 16px",
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "#E8EDF5"
    }`,
  },

  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    overflowX: "auto",
    paddingBottom: 4,
    marginBottom: 14,
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },

  filterButton: {
    flexShrink: 0,
    minHeight: 34,
    padding: "7px 13px",
    borderRadius: 9,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "#E2E8F0"
    }`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "#FFFFFF",
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#64748B",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 700,
    transition: "all .15s ease",
    "&:hover": {
      borderColor: "rgba(29,78,216,.35)",
      color: "#1D4ED8",
    },
  },

  filterButtonActive: {
    color: "#FFFFFF",
    borderColor: "#1D4ED8",
    background: "#1D4ED8",
    boxShadow: "0 6px 16px rgba(29,78,216,.18)",
    "&:hover": {
      color: "#FFFFFF",
      background: "#1E40AF",
    },
  },

  ledgerList: {
    display: "flex",
    flexDirection: "column",
  },

  ledgerItem: {
    display: "grid",
    gridTemplateColumns: "44px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    padding: "14px 4px",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "#F1F5F9"
    }`,
    "&:last-child": {
      borderBottom: "none",
    },
  },

  ledgerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    fontWeight: 900,
  },

  ledgerDescription: {
    minWidth: 0,
  },

  ledgerTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F1F5F9"
        : "#0F172A",
    fontSize: "0.82rem",
    fontWeight: 750,
    lineHeight: 1.35,
  },

  ledgerMeta: {
    marginTop: 3,
    color: "#94A3B8",
    fontSize: "0.69rem",
    lineHeight: 1.4,
  },

  ledgerValue: {
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 16,
    paddingTop: 14,
    borderTop: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "#F1F5F9"
    }`,
  },

  pageButton: {
    minHeight: 34,
    padding: "7px 13px",
    borderRadius: 9,
    border: "1px solid rgba(29,78,216,.22)",
    background: "rgba(29,78,216,.06)",
    color: "#1D4ED8",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 750,
    "&:disabled": {
      opacity: 0.38,
      cursor: "not-allowed",
    },
  },

  emptyLedger: {
    padding: "30px 16px",
    textAlign: "center",
    color: "#94A3B8",
    fontSize: "0.8rem",
    lineHeight: 1.6,
  },
}));

const ALERT_COLORS = { warning: "#EAB308", danger: "#F97316", critical: "#EF4444" };

const AICreditsPage = () => {
  const classes = useStyles();
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [ledger, setLedger] = useState({
    entries: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    },
    summary: {
      totalCredits: 0,
      totalDebits: 0,
      totalEntries: 0,
      currentBalance: 0
    }
  });

  const [ledgerFilter, setLedgerFilter] =
    useState("all");

  const [ledgerPage, setLedgerPage] =
    useState(1);

  const [ledgerLoading, setLedgerLoading] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const getLedgerParams = (
    filter,
    page
  ) => {
    const params = {
      page,
      limit: 20
    };

    if (filter === "credit") {
      params.direction = "credit";
    }

    if (filter === "debit") {
      params.direction = "debit";
    }

    if (filter === "subscription") {
      params.reason = "subscription_payment";
    }

    if (filter === "package") {
      params.reason = "credit_package";
    }

    if (filter === "ai") {
      params.reason = "ai_response";
    }

    if (filter === "admin") {
      params.reason = "admin_adjustment";
    }

    return params;
  };

  const loadLedger = async (
    filter = ledgerFilter,
    page = ledgerPage
  ) => {
    setLedgerLoading(true);

    try {
      const response = await api.get(
        "/ai-credits/history",
        {
          params: getLedgerParams(
            filter,
            page
          )
        }
      );

      setLedger({
        entries:
          response.data?.entries || [],
        pagination:
          response.data?.pagination || {
            page,
            limit: 20,
            total: 0,
            pages: 0
          },
        summary:
          response.data?.summary || {
            totalCredits: 0,
            totalDebits: 0,
            totalEntries: 0,
            currentBalance: 0
          }
      });
    } catch (error) {
      console.error(
        "Erro ao carregar extrato de créditos:",
        error
      );
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/ai-credits/balance")
      .then(response =>
        setBalance(response.data)
      )
      .catch(() => {});

    api
      .get("/ai-credits/packages")
      .then(response =>
        setPackages(response.data)
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLedger(
      ledgerFilter,
      ledgerPage
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ledgerFilter,
    ledgerPage
  ]);

  const [paymentModal, setPaymentModal] = useState(null);
  const [cpfCnpj, setCpfCnpj] = useState("");

  const handleBuy = async (pkg) => {
    if (!cpfCnpj.trim()) {
      setMsg({
        type: "error",
        text: "⚠️ Informe seu CPF ou CNPJ para continuar a compra."
      });

      const input = document.querySelector("input[placeholder*='000']");
      if (input) {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
        input.focus();
      }

      return;
    }

    setLoading(true); setMsg(null);
    try {
      const { data } = await api.post("/asaas/purchase", {
        packageId: pkg.id,
        cpfCnpj: cpfCnpj || undefined,
      });
      setPaymentModal({ ...data, pkg });
    } catch (err) {
      console.error("ERRO ASAAS", err);
      console.error("RESPOSTA", err?.response?.data);

      const errMsg = err?.response?.data?.error || "Erro ao iniciar pagamento.";
      setMsg({ type: "error", text: `❌ ${errMsg}` });
    }
    setLoading(false);
  };

  const checkStatus = async (paymentId) => {
    try {
      const { data } = await api.get(`/asaas/payment/${paymentId}/status`);
      if (data.status === "paid") {
        setPaymentModal(null);
        const r = await api.get("/ai-credits/balance");
        setBalance(r.data);
        setLedgerPage(1);
        await loadLedger(ledgerFilter, 1);
        toast.success("✅ Créditos liberados!");
      } else {
        toast.info("Pagamento ainda pendente. Aguarde.");
      }
    } catch { toast.error("Erro ao verificar pagamento"); }
  };

  const getLedgerPresentation = entry => {
    const reason =
      entry?.reason ||
      entry?.source ||
      "movement";

    if (reason === "subscription_payment") {
      return {
        icon: "↗",
        title: "Créditos do plano",
        subtitle: "Assinatura Korax confirmada",
        color: "#16A34A",
        background: "rgba(34,197,94,.10)"
      };
    }

    if (reason === "credit_package") {
      return {
        icon: "⚡",
        title: "Pacote de créditos",
        subtitle: "Compra adicional confirmada",
        color: "#2563EB",
        background: "rgba(37,99,235,.10)"
      };
    }

    if (reason === "admin_adjustment") {
      return {
        icon: "✦",
        title: "Ajuste administrativo",
        subtitle: "Movimentação realizada pela Korax",
        color: "#D97706",
        background: "rgba(245,158,11,.12)"
      };
    }

    if (reason === "ai_response") {
      return {
        icon: "K",
        title:
          entry?.contactName
            ? `Atendimento de ${entry.contactName}`
            : "Resposta da Korax IA",
        subtitle:
          entry?.agentName ||
          "Consumo de inteligência artificial",
        color: "#DC2626",
        background: "rgba(239,68,68,.10)"
      };
    }

    return {
      icon:
        entry?.direction === "credit"
          ? "+"
          : "−",
      title:
        entry?.direction === "credit"
          ? "Entrada de créditos"
          : "Consumo de créditos",
      subtitle:
        entry?.source ||
        "Movimentação de créditos",
      color:
        entry?.direction === "credit"
          ? "#16A34A"
          : "#DC2626",
      background:
        entry?.direction === "credit"
          ? "rgba(34,197,94,.10)"
          : "rgba(239,68,68,.10)"
    };
  };

  const formatLedgerDate = value => {
    if (!value) {
      return "Data não informada";
    }

    return new Date(value).toLocaleString(
      "pt-BR",
      {
        dateStyle: "short",
        timeStyle: "short"
      }
    );
  };

  const filterOptions = [
    {
      id: "all",
      label: "Todos"
    },
    {
      id: "credit",
      label: "Entradas"
    },
    {
      id: "debit",
      label: "Saídas"
    },
    {
      id: "subscription",
      label: "Assinatura"
    },
    {
      id: "package",
      label: "Pacotes"
    },
    {
      id: "ai",
      label: "Korax IA"
    },
    {
      id: "admin",
      label: "Ajustes"
    }
  ];

  const pct = balance ? Math.min(Math.round((balance.balance / (balance.monthlyIncluded || 1)) * 100), 100) : 0;
  const barColor = balance?.alertLevel ? ALERT_COLORS[balance.alertLevel] : "#22C55E";

  return (
    <KoraxPage className={classes.root}>
      {/* MODAL PAGAMENTO */}
      {paymentModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", maxWidth: 480, width: "100%", boxShadow: "0 24px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: "1.8rem", marginBottom: 8 }}>⚡</div>
              <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#0F172A" }}>{paymentModal.pkg?.name}</div>
              <div style={{ color: "#64748B", fontSize: "0.85rem", marginTop: 4 }}>+{paymentModal.pkg?.credits?.toLocaleString()} créditos • R$ {Number(paymentModal.pkg?.price).toFixed(2).replace(".",",")}</div>
            </div>

            {/* PIX */}
            {paymentModal.pixQrCode && (
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#16A34A", marginBottom: 10 }}>📱 Pagar com Pix</div>
                <img src={`data:image/png;base64,${paymentModal.pixQrCode}`} alt="QR Code Pix" style={{ width: 180, height: 180, borderRadius: 12, border: "1px solid #E8EDF5" }} />
                {paymentModal.pixKey && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginBottom: 4 }}>Ou copie o código:</div>
                    <div style={{ background: "#F8FAFC", border: "1px solid #E8EDF5", borderRadius: 8, padding: "8px 12px", fontSize: "0.72rem", color: "#1E293B", wordBreak: "break-all", cursor: "pointer" }}
                      onClick={() => { navigator.clipboard.writeText(paymentModal.pixKey); toast.success("Código copiado!"); }}>
                      {paymentModal.pixKey.substring(0, 60)}...
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* LINK FATURA */}
            {paymentModal.invoiceUrl && (
              <a href={paymentModal.invoiceUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: "block", textAlign: "center", background: "#1D4ED8", color: "#fff", textDecoration: "none", borderRadius: 10, padding: "12px", fontWeight: 700, fontSize: "0.88rem", marginBottom: 12 }}>
                💳 Pagar com Cartão ou Boleto →
              </a>
            )}

            <button onClick={() => checkStatus(paymentModal.paymentId)}
              style={{ width: "100%", borderRadius: 10, fontWeight: 600, fontSize: "0.82rem", background: "rgba(22,163,74,0.08)", color: "#16A34A", border: "1px solid rgba(22,163,74,0.2)", padding: "10px", cursor: "pointer", marginBottom: 10 }}>
              ✅ Já paguei — verificar pagamento
            </button>

            <button onClick={() => setPaymentModal(null)}
              style={{ width: "100%", borderRadius: 10, fontWeight: 600, fontSize: "0.82rem", background: "transparent", color: "#94A3B8", border: "1px solid #E2E8F0", padding: "10px", cursor: "pointer" }}>
              Fechar
            </button>
          </div>
        </div>
      )}
      <div className={classes.inner}>

        <KoraxPageHeader
          title="Créditos IA"
          subtitle="Gerencie e acompanhe o consumo da inteligência artificial Korax."
        />

        {msg && (
          <div style={{ padding: "12px 16px", borderRadius: 10, fontSize: "0.84rem", fontWeight: 600, background: msg.type === "success" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${msg.type === "success" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`, color: msg.type === "success" ? "#16A34A" : "#DC2626" }}>
            {msg.text}
          </div>
        )}

        {/* SALDO */}
        {balance && (
          <div className={classes.card}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div className={classes.cardTitle}>Saldo disponível</div>
                <div className={classes.balanceNum}>{balance.balance.toLocaleString()}</div>
                <div style={{ fontSize: "0.8rem", color: "#94A3B8", marginTop: 4 }}>créditos restantes</div>
                <div className={classes.track}>
                  <div className={classes.fill} style={{ width: `${pct}%`, background: barColor }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "#94A3B8" }}>
                  <span>{pct}% restante</span>
                  <span>{balance.monthlyIncluded.toLocaleString()} inclusos no plano</span>
                </div>
                {balance.alertLevel && (
                  <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: `${ALERT_COLORS[balance.alertLevel]}15`, border: `1px solid ${ALERT_COLORS[balance.alertLevel]}30`, color: ALERT_COLORS[balance.alertLevel] }}>
                    {balance.alertLevel === "critical" ? "🔴 Créditos críticos! Adicione mais para não interromper a IA." : balance.alertLevel === "danger" ? "🟠 Poucos créditos restantes. Considere comprar mais." : "🟡 Seus créditos estão acabando."}
                  </div>
                )}
              </div>
              <div className={classes.statRow}>
                <div className={classes.statCard}>
                  <div className={classes.statNum}>{balance.totalConsumed.toLocaleString()}</div>
                  <div className={classes.statLabel}>Consumidos no total</div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statNum}>{balance.monthlyIncluded.toLocaleString()}</div>
                  <div className={classes.statLabel}>Inclusos no plano</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PACOTES */}
        <div>
          <div style={{ fontWeight: 700, fontSize: "1rem", color: "inherit", marginBottom: 10 }}>Comprar mais créditos</div>
          <div style={{ marginBottom: 18, background: cpfCnpj ? "rgba(34,197,94,0.04)" : "rgba(239,68,68,0.04)", border: `1.5px solid ${cpfCnpj ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.25)"}`, borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: "1.2rem" }}>{cpfCnpj ? "✅" : "⚠️"}</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: cpfCnpj ? "#16A34A" : "#DC2626", marginBottom: 5 }}>
                {cpfCnpj ? "CPF/CNPJ informado" : "Informe seu CPF ou CNPJ para comprar"}
              </div>
              <input
                style={{ border: `1.5px solid ${cpfCnpj ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, borderRadius: 9, padding: "9px 14px", fontSize: "0.88rem", outline: "none", width: "100%", maxWidth: 240, background: "#fff", fontFamily: "inherit", boxSizing: "border-box" }}
                placeholder="000.000.000-00 ou 00.000.000/0001-00"
                value={cpfCnpj}
                onChange={e => setCpfCnpj(e.target.value.replace(/[^0-9]/g, ""))}
                maxLength={14}
              />
            </div>
            <div style={{ fontSize: "0.72rem", color: "#94A3B8", maxWidth: 160, lineHeight: 1.5 }}>Obrigatório para emissão do boleto/PIX via Asaas</div>
          </div>
          <div className={classes.packagesGrid}>
            {packages.map((pkg) => (
              <div key={pkg.id} className={`${classes.packageCard} ${pkg.highlight ? classes.packageHighlight : ""}`}>
                {pkg.badge && <div className={classes.badge}>{pkg.badge}</div>}
                <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>⚡</div>
                <div style={{ fontSize: "0.8rem", color: "#94A3B8", marginBottom: 4 }}>{pkg.name}</div>
                <div className={classes.packageCredits}>+{pkg.credits.toLocaleString()}</div>
                <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginBottom: 4 }}>créditos</div>
                <div className={classes.packagePrice}>R$ {Number(pkg.price).toFixed(2).replace(".", ",")}</div>
                <button className={pkg.highlight ? classes.buyBtn : classes.buyBtnOutline} onClick={() => handleBuy(pkg)} disabled={loading}>
                  {loading ? "Processando..." : "Comprar"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* EXTRATO DE CRÉDITOS */}
        <div className={classes.card}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
              marginBottom: 16
            }}
          >
            <div>
              <div className={classes.cardTitle}>
                Extrato de créditos
              </div>

              <div
                style={{
                  color: "#94A3B8",
                  fontSize: "0.75rem",
                  lineHeight: 1.5
                }}
              >
                Acompanhe todas as entradas e utilizações da Korax IA.
              </div>
            </div>

            <button
              type="button"
              className={classes.pageButton}
              onClick={() =>
                loadLedger(
                  ledgerFilter,
                  ledgerPage
                )
              }
              disabled={ledgerLoading}
            >
              {ledgerLoading
                ? "Atualizando..."
                : "↻ Atualizar"}
            </button>
          </div>

          <div className={classes.ledgerSummary}>
            <div className={classes.ledgerSummaryCard}>
              <div
                style={{
                  color: "#16A34A",
                  fontSize: "1.28rem",
                  fontWeight: 850
                }}
              >
                +
                {Number(
                  ledger.summary.totalCredits || 0
                ).toLocaleString("pt-BR")}
              </div>

              <div className={classes.statLabel}>
                Créditos recebidos
              </div>
            </div>

            <div className={classes.ledgerSummaryCard}>
              <div
                style={{
                  color: "#DC2626",
                  fontSize: "1.28rem",
                  fontWeight: 850
                }}
              >
                −
                {Number(
                  ledger.summary.totalDebits || 0
                ).toLocaleString("pt-BR")}
              </div>

              <div className={classes.statLabel}>
                Créditos utilizados
              </div>
            </div>

            <div className={classes.ledgerSummaryCard}>
              <div
                style={{
                  color: "#1D4ED8",
                  fontSize: "1.28rem",
                  fontWeight: 850
                }}
              >
                {Number(
                  ledger.summary.currentBalance ||
                  balance?.balance ||
                  0
                ).toLocaleString("pt-BR")}
              </div>

              <div className={classes.statLabel}>
                Saldo atual
              </div>
            </div>

            <div className={classes.ledgerSummaryCard}>
              <div
                style={{
                  color: "#7C3AED",
                  fontSize: "1.28rem",
                  fontWeight: 850
                }}
              >
                {Number(
                  ledger.summary.totalEntries || 0
                ).toLocaleString("pt-BR")}
              </div>

              <div className={classes.statLabel}>
                Movimentações
              </div>
            </div>
          </div>

          <div className={classes.filtersRow}>
            {filterOptions.map(option => (
              <button
                key={option.id}
                type="button"
                className={`${classes.filterButton} ${
                  ledgerFilter === option.id
                    ? classes.filterButtonActive
                    : ""
                }`}
                onClick={() => {
                  setLedgerPage(1);
                  setLedgerFilter(option.id);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {ledgerLoading &&
          ledger.entries.length === 0 ? (
            <div className={classes.emptyLedger}>
              Carregando movimentações...
            </div>
          ) : ledger.entries.length === 0 ? (
            <div className={classes.emptyLedger}>
              <div
                style={{
                  fontSize: "1.4rem",
                  marginBottom: 7
                }}
              >
                ⚡
              </div>

              Nenhuma movimentação encontrada para este filtro.
            </div>
          ) : (
            <div className={classes.ledgerList}>
              {ledger.entries.map(entry => {
                const presentation =
                  getLedgerPresentation(entry);

                const amount = Number(
                  entry.amount ??
                  entry.creditsUsed ??
                  0
                );

                const isCredit =
                  entry.direction === "credit";

                return (
                  <div
                    key={entry.id}
                    className={classes.ledgerItem}
                  >
                    <div
                      className={classes.ledgerIcon}
                      style={{
                        color: presentation.color,
                        background:
                          presentation.background
                      }}
                    >
                      {presentation.icon}
                    </div>

                    <div
                      className={
                        classes.ledgerDescription
                      }
                    >
                      <div
                        className={classes.ledgerTitle}
                      >
                        {presentation.title}
                      </div>

                      <div
                        className={classes.ledgerMeta}
                      >
                        {presentation.subtitle}
                        {" • "}
                        {formatLedgerDate(
                          entry.createdAt
                        )}

                        {entry.paymentId
                          ? ` • Pagamento ${String(
                              entry.paymentId
                            ).slice(-8)}`
                          : ""}
                      </div>
                    </div>

                    <div
                      className={classes.ledgerValue}
                    >
                      <div
                        style={{
                          color: isCredit
                            ? "#16A34A"
                            : "#DC2626",
                          fontSize: "0.9rem",
                          fontWeight: 850
                        }}
                      >
                        {isCredit ? "+" : "−"}
                        {amount.toLocaleString(
                          "pt-BR"
                        )}
                      </div>

                      {entry.balanceAfter !== null &&
                        entry.balanceAfter !==
                          undefined && (
                          <div
                            style={{
                              marginTop: 3,
                              color: "#94A3B8",
                              fontSize: "0.66rem"
                            }}
                          >
                            Saldo{" "}
                            {Number(
                              entry.balanceAfter
                            ).toLocaleString(
                              "pt-BR"
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className={classes.paginationRow}>
            <button
              type="button"
              className={classes.pageButton}
              disabled={
                ledgerLoading ||
                ledger.pagination.page <= 1
              }
              onClick={() =>
                setLedgerPage(page =>
                  Math.max(1, page - 1)
                )
              }
            >
              ← Anterior
            </button>

            <div
              style={{
                color: "#94A3B8",
                fontSize: "0.72rem",
                textAlign: "center"
              }}
            >
              Página{" "}
              {Number(
                ledger.pagination.page || 1
              )}{" "}
              de{" "}
              {Math.max(
                Number(
                  ledger.pagination.pages || 0
                ),
                1
              )}
              <div
                style={{
                  marginTop: 2,
                  fontSize: "0.65rem"
                }}
              >
                {Number(
                  ledger.pagination.total || 0
                ).toLocaleString("pt-BR")}{" "}
                registros
              </div>
            </div>

            <button
              type="button"
              className={classes.pageButton}
              disabled={
                ledgerLoading ||
                ledger.pagination.page >=
                  ledger.pagination.pages ||
                ledger.pagination.pages === 0
              }
              onClick={() =>
                setLedgerPage(page =>
                  page + 1
                )
              }
            >
              Próxima →
            </button>
          </div>
        </div>

      </div>
    </KoraxPage>
  );
};

export default AICreditsPage;
